// Iter 48 — "Da sistemare": stati, azioni e sezioni UI condivise tra il
// dialog della Home e la pagina dedicata /da-sistemare.
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const notifyChanged = () => {
  try { window.dispatchEvent(new Event("da-sistemare-changed")); } catch { /* ignore */ }
};

export function usePendingFixes(enabled) {
  // Iter 33: dipendenti "da confermare" inseriti dagli utenti abilitati
  const [pendingEmps, setPendingEmps] = useState([]);
  // Iter 34: commesse "da confermare" create dagli utenti abilitati
  const [pendingComms, setPendingComms] = useState([]);
  // Iter 35: commesse usate nei rapportini ma MAI create in anagrafica
  const [missingComms, setMissingComms] = useState([]);
  // Iter 36: nomi dipendente nei rapportini senza scheda in anagrafica
  const [missingEmps, setMissingEmps] = useState([]);
  // Iter 47b: commesse attive SENZA descrizione del lavoro
  const [noDescComms, setNoDescComms] = useState([]);
  // Iter 47c: clienti in anagrafica SENZA stabilimento/codice EG-XX
  const [noStabClienti, setNoStabClienti] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [busy, setBusy] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    const [emps, comms, miss, missEmp, noDesc, noStab] = await Promise.all([
      api.get("/employees/pending").then(({ data }) => data).catch(() => []),
      api.get("/commesse/pending").then(({ data }) => data).catch(() => []),
      api.get("/commesse/missing-from-rapportini").then(({ data }) => data).catch(() => []),
      api.get("/employees/missing-from-rapportini").then(({ data }) => data).catch(() => []),
      api.get("/admin/commesse-no-descrizione").then(({ data }) => data).catch(() => []),
      api.get("/admin/clienti-no-stabilimento").then(({ data }) => data).catch(() => []),
    ]);
    const e = Array.isArray(emps) ? emps : [];
    const c = Array.isArray(comms) ? comms : [];
    const m = (Array.isArray(miss) ? miss : []).map((x) => ({
      ...x,
      cliente: x.cliente_suggerito || "",
    }));
    const me = (Array.isArray(missEmp) ? missEmp : []).map((x) => ({
      ...x,
      scelto: x.suggerito?.id || "",
    }));
    // Iter 47b: le -OFF ereditano la descrizione dalla madre al salvataggio
    // → mostra solo una voce per coppia (preferendo la madre)
    const ndRaw = (Array.isArray(noDesc) ? noDesc : []).map((x) => ({
      ...x,
      descrizione_nuova: "",
    }));
    const ndCodes = new Set(ndRaw.map((x) => x.codice));
    const nd = ndRaw.filter(
      (x) => !(x.codice.endsWith("-OFF") && ndCodes.has(x.codice.replace(/-OFF$/, "")))
    );
    const ns = (Array.isArray(noStab) ? noStab : []).map((x) => ({
      ...x,
      stab_codice: "",
      stab_nome: "",
    }));
    setPendingEmps(e);
    setPendingComms(c);
    setMissingComms(m);
    setMissingEmps(me);
    setNoDescComms(nd);
    setNoStabClienti(ns);
    if (me.length > 0) {
      api.get("/employees")
        .then(({ data }) => setAllEmployees(Array.isArray(data) ? data : []))
        .catch(() => {});
    }
    setLoaded(true);
    return e.length + c.length + m.length + me.length + nd.length + ns.length;
  }, []);

  useEffect(() => {
    if (enabled) load();
  }, [enabled, load]);

  const confirmPending = async (emp) => {
    setBusy(emp.id);
    try {
      await api.put(`/employees/${emp.id}`, {
        nome: (emp.nome || "").trim(),
        cognome: (emp.cognome || "").trim(),
        ruolo: emp.ruolo || "",
        attivo: emp.attivo !== false,
        mostra_nei_rapportini: emp.mostra_nei_rapportini !== false,
        aliases: emp.aliases || [],
        iban: emp.iban || "",
      });
      await api.post(`/employees/${emp.id}/confirm`);
      toast.success(`Dipendente ${emp.cognome} ${emp.nome} confermato`);
      setPendingEmps((prev) => prev.filter((e) => e.id !== emp.id));
      notifyChanged();
    } catch {
      toast.error("Conferma non riuscita");
    } finally {
      setBusy(null);
    }
  };

  const rejectPending = async (emp) => {
    if (!window.confirm(`Eliminare il dipendente ${emp.cognome} ${emp.nome}?\n\nVerranno rimosse anche le sue righe dai rapportini e i rapportini rimasti vuoti finiranno nel cestino (recuperabili 30 giorni).`)) return;
    setBusy(emp.id);
    try {
      const { data } = await api.delete(`/employees/${emp.id}?cascade=true`);
      const nT = data?.rapportini_cestinati || 0;
      toast.success(
        `Dipendente ${emp.cognome} ${emp.nome} eliminato${nT > 0 ? ` · ${nT} rapportin${nT === 1 ? "o cestinato" : "i cestinati"}` : ""}`
      );
      setPendingEmps((prev) => prev.filter((e) => e.id !== emp.id));
      notifyChanged();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Eliminazione non riuscita");
    } finally {
      setBusy(null);
    }
  };

  const confirmPendingComm = async (cm) => {
    setBusy(cm.id);
    try {
      await api.put(`/commesse/${cm.id}`, {
        codice: cm.codice,
        descrizione: cm.descrizione || "",
        cliente: (cm.cliente || "").trim(),
        tipo_struttura: cm.tipo_struttura || "cantiere",
        attiva: cm.attiva !== false,
      });
      await api.post(`/commesse/${cm.id}/confirm`);
      toast.success(`Commessa ${cm.codice} confermata`);
      setPendingComms((prev) => prev.filter((c) => c.id !== cm.id));
      notifyChanged();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Conferma non riuscita");
    } finally {
      setBusy(null);
    }
  };

  const rejectPendingComm = async (cm) => {
    if (!window.confirm(`Eliminare la commessa ${cm.codice}?\n\nVerranno rimosse anche le righe dei rapportini che la usano e i rapportini rimasti vuoti finiranno nel cestino (recuperabili 30 giorni).`)) return;
    setBusy(cm.id);
    try {
      const { data } = await api.delete(`/commesse/${cm.id}?cascade=true`);
      const nT = data?.rapportini_cestinati || 0;
      toast.success(
        `Commessa ${cm.codice} eliminata${nT > 0 ? ` · ${nT} rapportin${nT === 1 ? "o cestinato" : "i cestinati"}` : ""}`
      );
      setPendingComms((prev) => prev.filter((c) => c.id !== cm.id));
      notifyChanged();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Eliminazione non riuscita");
    } finally {
      setBusy(null);
    }
  };

  const createMissingComm = async (mc) => {
    if (!(mc.descrizione || "").trim()) {
      toast.error(`Inserisci la descrizione del lavoro per creare la commessa ${mc.codice}`);
      return;
    }
    if (!(mc.cliente || "").trim()) {
      toast.error(`Inserisci il cliente per creare la commessa ${mc.codice}`);
      return;
    }
    setBusy(mc.codice);
    try {
      const codice = mc.codice.toUpperCase();
      const { data } = await api.post("/commesse", {
        codice,
        descrizione: (mc.descrizione || "").trim(),
        cliente: (mc.cliente || "").trim(),
        tipo_struttura: codice.endsWith("-OFF") ? "officina" : "cantiere",
        attiva: true,
      });
      const n = data?.righe_ricollegate || 0;
      toast.success(
        `Commessa ${codice} creata${n > 0 ? ` · ${n} rapportin${n === 1 ? "o ricollegato" : "i ricollegati"}` : ""}`
      );
      setMissingComms((prev) => prev.filter((c) => c.codice !== mc.codice));
      notifyChanged();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Creazione non riuscita");
    } finally {
      setBusy(null);
    }
  };

  const rebindMissingEmp = async (me) => {
    if (!me.scelto) {
      toast.error("Scegli il dipendente a cui ricollegare il nome");
      return;
    }
    setBusy(me.nome);
    try {
      const { data } = await api.post("/employees/rebind-righe", {
        nome: me.nome,
        employee_id: me.scelto,
      });
      const n = data?.rapportini_ricollegati || 0;
      toast.success(
        `"${me.nome}" ricollegato a ${data?.employee_nome || "dipendente"} · ${n} rapportin${n === 1 ? "o" : "i"}${data?.alias_aggiunto ? " · alias salvato" : ""}`
      );
      setMissingEmps((prev) => prev.filter((x) => x.nome !== me.nome));
      notifyChanged();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Ricollegamento non riuscito");
    } finally {
      setBusy(null);
    }
  };

  const fixNoDescComm = async (cm) => {
    if (!(cm.descrizione_nuova || "").trim()) {
      toast.error("Inserisci la descrizione del lavoro");
      return;
    }
    setBusy(cm.id);
    try {
      const { data } = await api.post(`/commesse/${cm.id}/descrizione`, {
        descrizione: cm.descrizione_nuova.trim(),
      });
      toast.success(
        `Descrizione salvata su ${cm.codice}${data?.sibling_codice ? ` (e ${data.sibling_codice})` : ""}`
      );
      setNoDescComms((prev) =>
        prev.filter((c) => c.id !== cm.id && c.codice !== data?.sibling_codice)
      );
      notifyChanged();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Salvataggio non riuscito");
    } finally {
      setBusy(null);
    }
  };

  const fixNoStabCliente = async (cl) => {
    const cod = (cl.stab_codice || "").replace(/\D/g, "");
    if (!cod || cod.length > 2) {
      toast.error("Inserisci il codice stabilimento a 2 cifre (es. 71)");
      return;
    }
    setBusy(cl.id);
    try {
      const { data } = await api.post(`/clienti/${cl.id}/assign-codice`, {
        codice: cod.padStart(2, "0"),
        stabilimento_nome: (cl.stab_nome || "").trim(),
      });
      const n = data?.commesse_rebound || 0;
      toast.success(
        `Codice ${data.codice} assegnato a ${cl.nome}${n > 0 ? ` · ${n} commess${n === 1 ? "a ricollegata" : "e ricollegate"}` : ""}`
      );
      setNoStabClienti((prev) => prev.filter((c) => c.id !== cl.id));
      notifyChanged();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.message) {
        toast.error(detail.message);
      } else {
        toast.error(typeof detail === "string" ? detail : "Assegnazione non riuscita");
      }
    } finally {
      setBusy(null);
    }
  };

  const total =
    pendingEmps.length + pendingComms.length + missingComms.length +
    missingEmps.length + noDescComms.length + noStabClienti.length;

  return {
    pendingEmps, setPendingEmps,
    pendingComms, setPendingComms,
    missingComms, setMissingComms,
    missingEmps, setMissingEmps,
    noDescComms, setNoDescComms,
    noStabClienti, setNoStabClienti,
    allEmployees,
    busy, loaded, total, load,
    confirmPending, rejectPending,
    confirmPendingComm, rejectPendingComm,
    createMissingComm, rebindMissingEmp,
    fixNoDescComm, fixNoStabCliente,
  };
}

export function PendingFixesSections({ pf }) {
  const {
    pendingEmps, setPendingEmps, pendingComms, setPendingComms,
    missingComms, setMissingComms, missingEmps, setMissingEmps,
    noDescComms, setNoDescComms, noStabClienti, setNoStabClienti,
    allEmployees, busy,
    confirmPending, rejectPending, confirmPendingComm, rejectPendingComm,
    createMissingComm, rebindMissingEmp, fixNoDescComm, fixNoStabCliente,
  } = pf;

  return (
    <>
      {pendingEmps.map((emp) => (
        <div
          key={emp.id}
          className="rounded-lg border border-amber-200 bg-amber-50 p-3"
          data-testid={`pending-employee-${emp.id}`}
        >
          <div className="text-sm text-amber-900">
            Il sig. <span className="font-bold">{emp.created_by_name || "un utente"}</span> ha
            inserito il nuovo dipendente{" "}
            <span className="font-bold">{emp.cognome} {emp.nome}</span>
            {emp.ruolo ? <span className="text-amber-700"> ({emp.ruolo})</span> : null}{" "}
            <span className="italic">(da confermare)</span>
            {emp.created_at && (
              <span className="text-xs text-amber-700">
                {" "}· {new Date(emp.created_at).toLocaleDateString("it-IT")}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            <input
              type="text"
              value={emp.cognome || ""}
              onChange={(e) => setPendingEmps((prev) => prev.map((x) => (x.id === emp.id ? { ...x, cognome: e.target.value } : x)))}
              placeholder="Cognome"
              className="h-9 w-32 flex-1 min-w-[100px] rounded-md border border-slate-300 px-2 text-sm"
              data-testid={`pending-employee-cognome-${emp.id}`}
            />
            <input
              type="text"
              value={emp.nome || ""}
              onChange={(e) => setPendingEmps((prev) => prev.map((x) => (x.id === emp.id ? { ...x, nome: e.target.value } : x)))}
              placeholder="Nome"
              className="h-9 w-32 flex-1 min-w-[100px] rounded-md border border-slate-300 px-2 text-sm"
              data-testid={`pending-employee-nome-${emp.id}`}
            />
            <input
              type="text"
              value={emp.ruolo || ""}
              onChange={(e) => setPendingEmps((prev) => prev.map((x) => (x.id === emp.id ? { ...x, ruolo: e.target.value } : x)))}
              placeholder="Ruolo (es. Operaio)"
              className="h-9 w-36 flex-1 min-w-[110px] rounded-md border border-slate-300 px-2 text-sm"
              data-testid={`pending-employee-ruolo-${emp.id}`}
            />
          </div>
          <div className="flex gap-2 mt-2">
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 h-9"
              disabled={busy === emp.id || !(emp.cognome || "").trim() || !(emp.nome || "").trim()}
              onClick={() => confirmPending(emp)}
              data-testid={`confirm-employee-${emp.id}`}
            >
              {busy === emp.id ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-1" />
              )}
              Salva e conferma
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-9 text-red-600 border-red-200 hover:bg-red-50"
              disabled={busy === emp.id}
              onClick={() => rejectPending(emp)}
              data-testid={`reject-employee-${emp.id}`}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Elimina
            </Button>
          </div>
        </div>
      ))}
      {pendingComms.map((cm) => (
        <div
          key={cm.id}
          className="rounded-lg border border-indigo-200 bg-indigo-50 p-3"
          data-testid={`pending-commessa-${cm.id}`}
        >
          <div className="text-sm text-indigo-900">
            Il sig. <span className="font-bold">{cm.created_by_name || "un utente"}</span> ha
            inserito la nuova commessa{" "}
            <span className="font-bold font-mono">{cm.codice}</span>
            {cm.cliente ? <span className="text-indigo-700"> · cliente {cm.cliente}</span> : null}
            {cm.descrizione ? <span className="text-indigo-700"> · {cm.descrizione}</span> : null}{" "}
            <span className="italic">(da confermare)</span>
            {cm.created_at && (
              <span className="text-xs text-indigo-700">
                {" "}· {new Date(cm.created_at).toLocaleDateString("it-IT")}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            <input
              type="text"
              value={cm.descrizione || ""}
              onChange={(e) => setPendingComms((prev) => prev.map((x) => (x.id === cm.id ? { ...x, descrizione: e.target.value } : x)))}
              placeholder="Descrizione"
              className="h-9 flex-1 min-w-[130px] rounded-md border border-slate-300 px-2 text-sm"
              data-testid={`pending-commessa-descrizione-${cm.id}`}
            />
            <input
              type="text"
              value={cm.cliente || ""}
              onChange={(e) => setPendingComms((prev) => prev.map((x) => (x.id === cm.id ? { ...x, cliente: e.target.value } : x)))}
              placeholder="Cliente"
              className="h-9 flex-1 min-w-[130px] rounded-md border border-slate-300 px-2 text-sm"
              data-testid={`pending-commessa-cliente-${cm.id}`}
            />
          </div>
          <div className="flex gap-2 mt-2">
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 h-9"
              disabled={busy === cm.id}
              onClick={() => confirmPendingComm(cm)}
              data-testid={`confirm-commessa-${cm.id}`}
            >
              {busy === cm.id ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-1" />
              )}
              Salva e conferma
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-9 text-red-600 border-red-200 hover:bg-red-50"
              disabled={busy === cm.id}
              onClick={() => rejectPendingComm(cm)}
              data-testid={`reject-commessa-${cm.id}`}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Elimina
            </Button>
          </div>
        </div>
      ))}
      {missingComms.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="missing-commesse-block">
          <div className="text-sm font-bold text-red-900 mb-2">
            ⚠️ {missingComms.length === 1
              ? "È stata usata 1 commessa NON creata in anagrafica"
              : `Sono state usate ${missingComms.length} commesse NON create in anagrafica`}
            : creale subito per sistemare i rapportini.
          </div>
          <div className="space-y-2">
            {missingComms.map((mc) => (
              <div
                key={mc.codice}
                className="rounded-md border border-red-200 bg-white p-2.5"
                data-testid={`missing-commessa-${mc.codice}`}
              >
                <div className="text-sm text-red-900">
                  <span className="font-bold font-mono">{mc.codice}</span>
                  {" "}· usata in {mc.rapportini_count} rapportin{mc.rapportini_count === 1 ? "o" : "i"}
                  {mc.numeri?.length ? (
                    <span className="text-xs text-red-700"> (nn. {mc.numeri.join(", ")}{mc.rapportini_count > mc.numeri.length ? ", …" : ""})</span>
                  ) : null}
                  {mc.autori?.length ? (
                    <span className="text-xs text-red-700"> · inserita da {mc.autori.join(", ")}</span>
                  ) : null}
                </div>
                <div className="flex gap-2 mt-2 items-center">
                  <input
                    type="text"
                    value={mc.descrizione || ""}
                    onChange={(e) =>
                      setMissingComms((prev) =>
                        prev.map((c) => (c.codice === mc.codice ? { ...c, descrizione: e.target.value } : c))
                      )
                    }
                    placeholder="Descrizione lavoro (obbligatoria)"
                    className="h-9 flex-1 min-w-0 rounded-md border border-slate-300 px-2 text-sm"
                    data-testid={`missing-commessa-descrizione-${mc.codice}`}
                  />
                  <input
                    type="text"
                    value={mc.cliente || ""}
                    onChange={(e) =>
                      setMissingComms((prev) =>
                        prev.map((c) => (c.codice === mc.codice ? { ...c, cliente: e.target.value } : c))
                      )
                    }
                    placeholder="Cliente (obbligatorio)"
                    className="h-9 flex-1 min-w-0 rounded-md border border-slate-300 px-2 text-sm"
                    data-testid={`missing-commessa-cliente-${mc.codice}`}
                  />
                  <Button
                    size="sm"
                    className="bg-[#0044FF] hover:bg-[#0033CC] h-9 shrink-0"
                    disabled={busy === mc.codice}
                    onClick={() => createMissingComm(mc)}
                    data-testid={`create-missing-commessa-${mc.codice}`}
                  >
                    {busy === mc.codice ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4 mr-1" />
                    )}
                    Crea subito
                  </Button>
                </div>
                {mc.cliente_suggerito && (
                  <div className="text-[11px] text-emerald-700 mt-1">
                    Cliente suggerito dalla famiglia EG-{(mc.codice.match(/^EG-(\d{2})-/) || [])[1] || "XX"}: {mc.cliente_suggerito}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {noDescComms.length > 0 && (
        <div className="rounded-lg border border-orange-200 bg-orange-50 p-3" data-testid="no-desc-commesse-block">
          <div className="text-sm font-bold text-orange-900 mb-2">
            ✏️ {noDescComms.length === 1
              ? "C'è 1 commessa senza descrizione del lavoro"
              : `Ci sono ${noDescComms.length} commesse senza descrizione del lavoro`}
            : completale per ripulire l'anagrafica.
          </div>
          <div className="space-y-2">
            {noDescComms.map((cm) => (
              <div
                key={cm.id}
                className="rounded-md border border-orange-200 bg-white p-2.5"
                data-testid={`no-desc-commessa-${cm.codice}`}
              >
                <div className="text-sm text-orange-900">
                  <span className="font-bold font-mono">{cm.codice}</span>
                  {cm.cliente ? (
                    <span className="text-xs text-orange-700"> · cliente {cm.cliente}</span>
                  ) : null}
                </div>
                <div className="flex gap-2 mt-2 items-center">
                  <input
                    type="text"
                    value={cm.descrizione_nuova || ""}
                    onChange={(e) =>
                      setNoDescComms((prev) =>
                        prev.map((c) => (c.id === cm.id ? { ...c, descrizione_nuova: e.target.value } : c))
                      )
                    }
                    placeholder="Descrizione lavoro (es. Manutenzione forno)"
                    className="h-9 flex-1 min-w-0 rounded-md border border-slate-300 px-2 text-sm"
                    data-testid={`no-desc-input-${cm.codice}`}
                  />
                  <Button
                    size="sm"
                    className="bg-orange-600 hover:bg-orange-700 h-9 shrink-0"
                    disabled={busy === cm.id || !(cm.descrizione_nuova || "").trim()}
                    onClick={() => fixNoDescComm(cm)}
                    data-testid={`no-desc-save-${cm.codice}`}
                  >
                    {busy === cm.id ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4 mr-1" />
                    )}
                    Salva
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {noStabClienti.length > 0 && (
        <div className="rounded-lg border border-fuchsia-200 bg-fuchsia-50 p-3" data-testid="no-stab-clienti-block">
          <div className="text-sm font-bold text-fuchsia-900 mb-2">
            🏭 {noStabClienti.length === 1
              ? "C'è 1 cliente senza stabilimento (codice EG-XX)"
              : `Ci sono ${noStabClienti.length} clienti senza stabilimento (codice EG-XX)`}
            : assegna il codice per completare l'anagrafica.
          </div>
          <div className="space-y-2">
            {noStabClienti.map((cl) => (
              <div
                key={cl.id}
                className="rounded-md border border-fuchsia-200 bg-white p-2.5"
                data-testid={`no-stab-cliente-${cl.id}`}
              >
                <div className="text-sm text-fuchsia-900">
                  <span className="font-bold">{cl.nome}</span>
                  {cl.partita_iva ? (
                    <span className="text-xs text-fuchsia-700"> · P.IVA {cl.partita_iva}</span>
                  ) : null}
                </div>
                <div className="flex gap-2 mt-2 items-center">
                  <input
                    type="text"
                    value={cl.stab_codice || ""}
                    onChange={(e) =>
                      setNoStabClienti((prev) =>
                        prev.map((c) => (c.id === cl.id ? { ...c, stab_codice: e.target.value } : c))
                      )
                    }
                    placeholder="XX"
                    maxLength={2}
                    className="h-9 w-16 shrink-0 rounded-md border border-slate-300 px-2 text-sm font-mono text-center"
                    data-testid={`no-stab-codice-${cl.id}`}
                  />
                  <input
                    type="text"
                    value={cl.stab_nome || ""}
                    onChange={(e) =>
                      setNoStabClienti((prev) =>
                        prev.map((c) => (c.id === cl.id ? { ...c, stab_nome: e.target.value } : c))
                      )
                    }
                    placeholder="Nome stabilimento (opzionale)"
                    className="h-9 flex-1 min-w-0 rounded-md border border-slate-300 px-2 text-sm"
                    data-testid={`no-stab-nome-${cl.id}`}
                  />
                  <Button
                    size="sm"
                    className="bg-fuchsia-600 hover:bg-fuchsia-700 h-9 shrink-0"
                    disabled={busy === cl.id || !(cl.stab_codice || "").trim()}
                    onClick={() => fixNoStabCliente(cl)}
                    data-testid={`no-stab-save-${cl.id}`}
                  >
                    {busy === cl.id ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4 mr-1" />
                    )}
                    Assegna
                  </Button>
                </div>
                <div className="text-[11px] text-fuchsia-700 mt-1">
                  Codice a 2 cifre delle commesse EG-XX-… · 99 = jolly per lavorazioni rare
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {missingEmps.length > 0 && (
        <div className="rounded-lg border border-violet-200 bg-violet-50 p-3" data-testid="missing-employees-block">
          <div className="text-sm font-bold text-violet-900 mb-2">
            ⚠️ {missingEmps.length === 1
              ? "C'è 1 nome dipendente non riconosciuto nei rapportini"
              : `Ci sono ${missingEmps.length} nomi dipendente non riconosciuti nei rapportini`}
            : ricollegali alla scheda giusta.
          </div>
          <div className="space-y-2">
            {missingEmps.map((me) => (
              <div
                key={me.nome}
                className="rounded-md border border-violet-200 bg-white p-2.5"
                data-testid={`missing-employee-${me.nome}`}
              >
                <div className="text-sm text-violet-900">
                  Nome <span className="font-bold">"{me.nome}"</span>
                  {" "}· usato in {me.rapportini_count} rapportin{me.rapportini_count === 1 ? "o" : "i"}
                  {me.numeri?.length ? (
                    <span className="text-xs text-violet-700"> (nn. {me.numeri.join(", ")}{me.rapportini_count > me.numeri.length ? ", …" : ""})</span>
                  ) : null}
                  {me.autori?.length ? (
                    <span className="text-xs text-violet-700"> · inserito da {me.autori.join(", ")}</span>
                  ) : null}
                </div>
                <div className="flex gap-2 mt-2 items-center">
                  <select
                    value={me.scelto || ""}
                    onChange={(e) =>
                      setMissingEmps((prev) =>
                        prev.map((x) => (x.nome === me.nome ? { ...x, scelto: e.target.value } : x))
                      )
                    }
                    className="h-9 flex-1 min-w-0 rounded-md border border-slate-300 px-2 text-sm bg-white"
                    data-testid={`missing-employee-select-${me.nome}`}
                  >
                    <option value="">— scegli dipendente —</option>
                    {allEmployees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.cognome} {emp.nome}
                      </option>
                    ))}
                  </select>
                  <Button
                    size="sm"
                    className="bg-violet-600 hover:bg-violet-700 h-9 shrink-0"
                    disabled={busy === me.nome || !me.scelto}
                    onClick={() => rebindMissingEmp(me)}
                    data-testid={`rebind-missing-employee-${me.nome}`}
                  >
                    {busy === me.nome ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4 mr-1" />
                    )}
                    Ricollega
                  </Button>
                </div>
                {me.suggerito && (
                  <div className="text-[11px] text-emerald-700 mt-1">
                    Suggerito (più simile): {me.suggerito.full_name}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
